import { User } from "firebase/auth"

// Passkey (WebAuthn) client for the admin-service relying party (VLT-2207, VLT-2637).
//
// These calls deliberately bypass Comm: /webauthn/auth/complete answers 401 for any failed assertion
// (a cancelled prompt, an unknown credential, an origin mismatch), and Comm treats every 401 as an
// expired session and signs the user out. A failed passkey attempt must not cost anyone their login.
const WEBAUTHN_BASE = 'https://api.admin.voltie.hu/webauthn'

// Mirrors the backend WEBAUTHN_RP_ID. A page may only use an RP ID equal to its own host or a parent of
// it, so passkeys work on admin.voltie.eu and the browser refuses them on localhost or *.web.app.
const RP_ID = 'voltie.eu'

interface CredentialDescriptorJSON {
  id: string
  type: PublicKeyCredentialType
  transports?: AuthenticatorTransport[]
}

interface CreationOptionsJSON extends Omit<PublicKeyCredentialCreationOptions, 'challenge' | 'user' | 'excludeCredentials'> {
  challenge: string
  user: { id: string, name: string, displayName: string }
  excludeCredentials?: CredentialDescriptorJSON[]
}

interface RequestOptionsJSON extends Omit<PublicKeyCredentialRequestOptions, 'challenge' | 'allowCredentials'> {
  challenge: string
  allowCredentials?: CredentialDescriptorJSON[]
}

// The dev server always shows the passkey UI so it can be worked on, although the browser refuses it there.
export function isPasskeyAvailable(): boolean {
  if (import.meta.env.DEV) return true
  const host = location.hostname
  return typeof window.PublicKeyCredential !== 'undefined'
    && window.isSecureContext
    && (host === RP_ID || host.endsWith(`.${RP_ID}`))
}

// The user dismissing the browser prompt is not an error worth showing.
export function isPasskeyCancelled(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')
}

function base64urlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  let binary = ''
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function toDescriptors(list?: CredentialDescriptorJSON[]): PublicKeyCredentialDescriptor[] {
  return (list ?? []).map(descriptor => ({ ...descriptor, id: base64urlToBuffer(descriptor.id) }))
}

async function post(path: string, body: unknown, authorization?: string) {
  const headers: { [key: string]: string } = { 'Content-Type': 'application/json' }
  if (authorization) headers.Authorization = authorization
  const response = await fetch(`${WEBAUTHN_BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body) })
  if (!response.ok) {
    // Each failure is a 401 whose body names the reason: missing_field, challenge_invalid,
    // credential_unknown or verification_failed (VLT-2940).
    const reason = await response.json().then(json => json?.error).catch(() => undefined)
    throw new Error(`${path} answered ${response.status}${reason ? ` (${reason})` : ''}`)
  }
  return response
}

// The complete endpoints take a flat body: the authenticator response fields at the top level next to
// the echoed challenge. The nested PublicKeyCredential JSON (fields under "response") is refused.
// auth/complete identifies the credential by credentialId, the browser's credential.id as is (unpadded
// base64url); register/complete takes no id at all and derives it from the attestation object.

// Binds a new passkey to the signed-in account. register/* is guarded by the Firebase ID token, which an
// admin needs too: their default Comm token is the server JWT, which the backend would not accept here.
export async function registerPasskey(user: User) {
  const authorization = `Bearer ${await user.getIdToken(true)}`
  const options: CreationOptionsJSON = await (await post('/register/begin', {}, authorization)).json()

  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      user: { ...options.user, id: base64urlToBuffer(options.user.id) },
      excludeCredentials: toDescriptors(options.excludeCredentials),
    },
  }) as PublicKeyCredential | null
  if (!credential) throw new Error('The browser returned no credential')

  const response = credential.response as AuthenticatorAttestationResponse
  await post('/register/complete', {
    challenge: options.challenge,
    type: credential.type,
    clientDataJSON: bufferToBase64url(response.clientDataJSON),
    attestationObject: bufferToBase64url(response.attestationObject),
  }, authorization)
}

// Usernameless sign-in: auth/begin sends no allowCredentials, so the browser offers every passkey it
// holds for voltie.eu, including ones created in the iOS or Android app. Resolves to a Firebase custom
// token for signInWithCustomToken.
export async function getPasskeyCustomToken(): Promise<string> {
  const options: RequestOptionsJSON = await (await post('/auth/begin', {})).json()

  const credential = await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      allowCredentials: toDescriptors(options.allowCredentials),
    },
  }) as PublicKeyCredential | null
  if (!credential) throw new Error('The browser returned no credential')

  const response = credential.response as AuthenticatorAssertionResponse
  const result: { firebaseCustomToken?: string } = await (await post('/auth/complete', {
    challenge: options.challenge,
    credentialId: credential.id,
    type: credential.type,
    clientDataJSON: bufferToBase64url(response.clientDataJSON),
    authenticatorData: bufferToBase64url(response.authenticatorData),
    signature: bufferToBase64url(response.signature),
    userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
  })).json()

  if (!result.firebaseCustomToken) throw new Error('auth/complete returned no custom token')
  return result.firebaseCustomToken
}
